/**
 * src/lib/interactiveChecklist.ts
 * Types and YAML parser for the InteractiveChecklist embedded component.
 *
 * Supported YAML schema (inside ```checklist ... ``` fenced blocks):
 *
 * --- Flat (no groups) ---
 * title: Minha lista
 * items:
 *   - Item A
 *   - Item B
 *
 * --- Grouped ---
 * title: Checklist completo
 * sections:
 *   - group: Motor
 *     items:
 *       - Verificar óleo
 *       - Verificar refrigerante
 *   - group: Pneus
 *     items:
 *       - Calibragem OK
 *   - items:
 *       - Documentos em dia   ← section without group title
 */

export interface ChecklistSection {
  /** Optional group heading. Omit for a flat / untitled section. */
  group?: string;
  items: string[];
}

export interface ChecklistData {
  /** Main heading displayed at the top of the card. */
  title?: string;
  sections: ChecklistSection[];
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function extractValue(line: string, prefix: string): string {
  return line.slice(prefix.length).trim().replace(/^['"]|['"]$/g, '');
}

/**
 * Minimal, indentation-aware YAML parser for the checklist schema.
 * Uses a single-pass state machine — no external dependency.
 */
function parseChecklistYaml(raw: string): ChecklistData | undefined {
  const lines = raw
    .split('\n')
    .map((l) => l.replace(/\t/g, '  '))
    .filter((l) => l.trim() && !l.trim().startsWith('#'));

  const data: ChecklistData = { sections: [] };
  let cur: ChecklistSection | null = null;
  let inSections = false;
  let inSectionItems = false; // true when reading item children of a section

  for (const rawLine of lines) {
    const indent = (rawLine.match(/^\s*/) ?? [''])[0].length;
    const l = rawLine.trim();

    // ── Root-level keys (indent 0) ──────────────────────────────────────
    if (indent === 0) {
      inSections = false;
      inSectionItems = false;
      if (l.startsWith('title:')) {
        data.title = extractValue(l, 'title:');
      } else if (l === 'items:') {
        // Flat format: single ungrouped section
        cur = { items: [] };
        data.sections.push(cur);
        inSectionItems = true; // items at indent 2
      } else if (l === 'sections:') {
        inSections = true;
      }
      continue;
    }

    // ── Flat items at indent 2 ───────────────────────────────────────────
    if (!inSections && inSectionItems && indent === 2 && l.startsWith('- ')) {
      cur!.items.push(l.slice(2).trim().replace(/^['"]|['"]$/g, ''));
      continue;
    }

    // ── Sections mode ────────────────────────────────────────────────────
    if (inSections) {
      // New section begins at indent 2 with a list bullet
      if (indent === 2 && l.startsWith('-')) {
        const rest = l.slice(1).trim();
        cur = { items: [] };
        data.sections.push(cur);
        inSectionItems = false;

        if (rest.startsWith('group:')) {
          cur.group = extractValue(rest, 'group:');
        } else if (rest === 'items:') {
          // `- items:` → items at indent 6 (standard YAML nesting)
          inSectionItems = true;
        }
        continue;
      }

      // Section properties at indent 4
      if (cur && indent === 4) {
        if (l.startsWith('group:')) {
          cur.group = extractValue(l, 'group:');
          continue;
        }
        if (l === 'items:') {
          inSectionItems = true;
          continue;
        }
        // Items directly at indent 4 (when section started with `- items:`)
        if (inSectionItems && l.startsWith('- ')) {
          cur.items.push(l.slice(2).trim().replace(/^['"]|['"]$/g, ''));
          continue;
        }
      }

      // Items at indent 6 (standard nesting under `    items:`)
      if (cur && inSectionItems && indent === 6 && l.startsWith('- ')) {
        cur.items.push(l.slice(2).trim().replace(/^['"]|['"]$/g, ''));
      }
    }
  }

  // Filter degenerate sections and validate
  data.sections = data.sections.filter((s) => s.items.length > 0);
  if (data.sections.length === 0) return undefined;

  return data;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Parses the raw YAML string from a ```checklist block and returns a
 * validated ChecklistData object, or undefined if parsing fails.
 */
export function parseChecklist(rawYaml: string): ChecklistData | undefined {
  try {
    return parseChecklistYaml(rawYaml.trim());
  } catch {
    return undefined;
  }
}

/**
 * Derives a stable localStorage sub-key from the checklist title.
 * Falls back to the numeric index if no title is provided.
 */
export function makeChecklistStorageKey(
  slug: string,
  title: string | undefined,
  anchorIndex: number
): string {
  const titleKey = title
    ? title
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 48)
    : String(anchorIndex);
  return `gas2:checklist:${slug}:${titleKey}`;
}


